import React from 'react';
import moment from 'moment';
import { Select } from 'antd';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import type { ColumnsType } from 'antd/es/table';
import { PERMISSIONS } from 'constants/permissions';
import { LoadingOutlined } from '@ant-design/icons';
import { HasAccess } from 'components/common/accessControl';
import { Button, Col, Popover, Row, Table, Tag, Typography } from 'components';
import { PurchaseOrderItemStatusBadge } from 'components/common/StatusBadge';
import { PurchasePricesDrawer } from './purchasePricesDrawer';
import { SuppliersDrawer } from './suppliersDrawer';
import { ClientsDrawer } from './clientsDrawer';
import { useItems } from './useItems';
import styles from 'pages/admin/purchaseOrders/styles.module.scss';

const { Text } = Typography;
const { Option, OptGroup } = Select;

type EntityTableType = {
  name: string;
  id: string;
};

export interface PurchaseOrderItemDataType {
  id: string;
  uuid: string | number;
  itemSKU: string;
  description: string;
  brand: EntityTableType;
  supplier: EntityTableType;
  averagePrice: number;
  createDate: string;
  orderConfirmationDate: string;
  expectedShippingDate: string;
  lastModifiedDate: string;
  purchasePriceFrom: string;
  purchasePriceTo: string;
  createDateFrom: any;
  createDateTo: any;
  orderConfirmationDateFrom: any;
  orderConfirmationDateTo: any;
  expectedShippingDateFrom: any;
  expectedShippingDateTo: any;
  closeToExpirationDate: boolean;
  deliveryDateTo: any;
  deliveryDateFrom: any;
}

export interface IItemsTable {
  filters: Record<string, any>;
  onRowSelect: (ids: React.Key[], values: Record<string, any>[]) => void;
}

export const ItemsTable = (props: IItemsTable) => {
  const { filters, onRowSelect } = props;
  const {
    handleTableChange,
    isLoading,
    data,
    clientDrawerState,
    supplierDrawerState,
    onSupplierDrawerExpand,
    onClientDrawerExpand,
    suppliersData,
    rowSelection,
    clientsData,
    onPurchasePriceDrawerExpand,
    purchaseDrawerState,
    orderSku,
    onGroupingChange,
  } = useItems({ filters, onRowSelect });

  const columns: ColumnsType<PurchaseOrderItemDataType> = [
    {
      title: 'Item SKU',
      dataIndex: 'itemSku',
      key: 'itemSku',
      width: 150,
      ellipsis: true,
      sorter: true,
      render: (value) => {
        return (
          <Text style={{ maxWidth: '150px' }} ellipsis={{ tooltip: true }}>
            {value}
          </Text>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'itemDescription',
      width: 200,
      ellipsis: true,
      render: (value) => {
        return (
          <Text style={{ maxWidth: '230px' }} ellipsis={{ tooltip: true }}>
            {value}
          </Text>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: true,
      width: 200,
      ellipsis: true,
      render: (value) => {
        return (
          <Text style={{ maxWidth: '230px' }} ellipsis={{ tooltip: true }}>
            {value}
          </Text>
        );
      },
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 130,
      ellipsis: true,
      render: (value) => {
        return (
          <Link to={`${ADMIN_ROUTES.brand.view}/${value?.id}/management`} target={'_blank'}>
            <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
              {value?.name}
            </Text>
          </Link>
        );
      },
    },
    {
      title: 'Suppliers',
      dataIndex: 'suppliers',
      key: 'suppliers',
      width: 200, // Set max width
      render: (suppliers) => {
        return (
          <Row align={'middle'} wrap={false} justify={'space-between'}>
            {!!suppliers?.length && (
              <Link to={`${ADMIN_ROUTES.suppliers.view}/${suppliers[0].id}/general`} target={'_blank'}>
                <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                  {suppliers[0].name}
                </Text>
              </Link>
            )}
            {suppliers?.length > 1 && (
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                className={'underline-button'}
                onClick={() => onSupplierDrawerExpand(suppliers)}
              >
                <Text underline type={'secondary'}>
                  +{suppliers?.length - 1}
                </Text>
              </Button>
            )}
          </Row>
        );
      },
    },
    {
      title: 'Delivery Deadline',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      minWidth: 180,
      sorter: true,
      render: (_, record) => {
        const { deliveryDateFrom, deliveryDateTo } = record;
        if (!deliveryDateFrom && !deliveryDateTo) {
          return null;
        }
        const formattedDeliveryDateFrom = deliveryDateFrom ? moment.utc(deliveryDateFrom).format('DD.MM.YYYY') : null;
        const formattedDeliveryDateTo = deliveryDateTo ? moment.utc(deliveryDateTo).format('DD.MM.YYYY') : null;
        return formattedDeliveryDateFrom && formattedDeliveryDateTo
          ? `${formattedDeliveryDateFrom}-${formattedDeliveryDateTo}`
          : `${formattedDeliveryDateFrom || formattedDeliveryDateTo}`;
      },
    },
    {
      title: 'Expected Shipping Date',
      dataIndex: 'expectedShippingDate',
      key: 'expectedShippingDateFrom',
      minWidth: 170,
      sorter: true,
      render: (_, record) => {
        const { expectedShippingDateFrom, expectedShippingDateTo } = record;
        let closeToExpirationDate = false;
        if (!expectedShippingDateFrom && !expectedShippingDateTo) {
          return null;
        }
        if (expectedShippingDateTo) {
          if (record.deliveryDateFrom && !closeToExpirationDate) {
            closeToExpirationDate = moment
              .utc(expectedShippingDateTo)
              .startOf('day')
              .isSameOrAfter(moment.utc(record.deliveryDateFrom).startOf('day'));
          }
          if (record.deliveryDateTo && !closeToExpirationDate) {
            closeToExpirationDate = moment
              .utc(expectedShippingDateTo)
              .startOf('day')
              .isSameOrAfter(moment.utc(record.deliveryDateTo).startOf('day'));
          }
        } else if (expectedShippingDateFrom) {
          if (record.deliveryDateFrom && !closeToExpirationDate) {
            closeToExpirationDate = moment
              .utc(expectedShippingDateFrom)
              .startOf('day')
              .isSameOrAfter(moment.utc(record.deliveryDateFrom).startOf('day'));
          }
          if (record.deliveryDateTo && !closeToExpirationDate) {
            closeToExpirationDate = moment
              .utc(expectedShippingDateFrom)
              .startOf('day')
              .isSameOrAfter(moment.utc(record.deliveryDateTo).startOf('day'));
          }
        }
        const formattedExpectedShippingDateFrom = expectedShippingDateFrom
          ? moment.utc(expectedShippingDateFrom).format('DD.MM.YYYY')
          : null;
        const formattedExpectedShippingDateTo = expectedShippingDateTo
          ? moment.utc(expectedShippingDateTo).format('DD.MM.YYYY')
          : null;

        const value =
          formattedExpectedShippingDateFrom && formattedExpectedShippingDateTo
            ? `${formattedExpectedShippingDateFrom}-${formattedExpectedShippingDateTo}`
            : `${formattedExpectedShippingDateFrom || formattedExpectedShippingDateTo}`;
        return <Typography style={closeToExpirationDate ? { color: 'red' } : {}}>{value}</Typography>;
      },
    },
    {
      title: 'Confirmed Date',
      dataIndex: 'createDate',
      key: 'createDate',
      minWidth: 170,
      sorter: true,
      render: (_, record) => {
        const { orderConfirmationDateFrom, orderConfirmationDateTo } = record;
        if (!orderConfirmationDateFrom && !orderConfirmationDateTo) {
          return null;
        }
        const formattedOrderConfirmationDateFrom = orderConfirmationDateFrom
          ? moment.utc(orderConfirmationDateFrom).format('DD.MM.YYYY')
          : null;
        const formattedOrderConfirmationDateTo = orderConfirmationDateTo
          ? moment.utc(orderConfirmationDateTo).format('DD.MM.YYYY')
          : null;
        return formattedOrderConfirmationDateFrom && formattedOrderConfirmationDateTo
          ? `${formattedOrderConfirmationDateFrom}-${formattedOrderConfirmationDateTo}`
          : `${formattedOrderConfirmationDateFrom || formattedOrderConfirmationDateTo}`;
      },
    },
    {
      title: 'Purchase Price',
      dataIndex: 'itemSku',
      key: 'itemSku',
      minWidth: 130,
      render: (itemSku, record) => {
        const { purchasePriceFrom, purchasePriceTo } = record;
        if (!purchasePriceFrom && !purchasePriceTo) {
          return null;
        }
        const priceLabel =
          purchasePriceFrom && purchasePriceTo
            ? `€${purchasePriceFrom}-${purchasePriceTo}`
            : `€${purchasePriceFrom || purchasePriceTo}`;

        return (
          <Button
            size="small"
            type="text"
            className="underline-button"
            onClick={(e) => {
              e.stopPropagation();
              onPurchasePriceDrawerExpand(itemSku);
            }}
          >
            <Text type="secondary" underline>
              {priceLabel}
            </Text>
          </Button>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'orderItemStatus',
      key: 'orderItemStatus',
      minWidth: 130,
      render: (status) => <PurchaseOrderItemStatusBadge type={status} />,
    },
    {
      title: 'Order Numbers',
      dataIndex: 'orders',
      key: 'orders',
      width: 160,
      ellipsis: true,
      render: (orders) => {
        return (
          <Row align={'middle'} justify={'space-between'}>
            <Col>
              {!!orders?.length && (
                <Link to={`${ADMIN_ROUTES.orders.view}/${orders[0]?.id}/general`} target={'_blank'}>
                  <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                    {orders[0]?.uuid}
                  </Text>
                </Link>
              )}
            </Col>
            {orders.length > 1 && (
              <Col>
                <Popover
                  content={
                    <Row style={{ maxWidth: 240 }} gutter={[12, 12]}>
                      {orders.slice(1).map((order: any) => (
                        <Col span={8} key={order.id}>
                          <Link to={`${ADMIN_ROUTES.orders.view}/${order?.id}/general`} target="_blank">
                            {order.uuid}
                          </Link>
                        </Col>
                      ))}
                    </Row>
                  }
                  title="Order Numbers"
                  trigger="click"
                >
                  <Tag className={styles.additionalOffersTag}>+{orders?.length - 1}</Tag>
                </Popover>
              </Col>
            )}
          </Row>
        );
      },
    },
    {
      title: 'Clients',
      dataIndex: 'clients',
      key: 'clients',
      width: 200, // Set max width
      render: (clients) => {
        return (
          <Row align={'middle'} wrap={false} justify={'space-between'}>
            {!!clients?.length && (
              <Link to={`${ADMIN_ROUTES.clients}/${clients[0].id}/general`} target={'_blank'}>
                <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                  {clients[0].name}
                </Text>
              </Link>
            )}
            {clients?.length > 1 && (
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                className={'underline-button'}
                onClick={() => onClientDrawerExpand(clients)}
              >
                <Text underline type={'secondary'}>
                  +{clients?.length - 1}
                </Text>
              </Button>
            )}
          </Row>
        );
      },
    },
  ];

  return (
    <Col span={24}>
      <Row gutter={[0, 12]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <Col>
              <Text style={{ color: '#202E42' }} className={'font-weight-800 font-size-16'}>
                Items
              </Text>
            </Col>
            <Col flex={'300px'}>
              <Row gutter={[0, 4]}>
                <Col span={24}>
                  <Text className={'font-weight-600'}>Grouped By</Text>
                </Col>
                <Col span={24}>
                  <Select style={{ width: '100%' }} defaultValue="NO_GROUP" onChange={onGroupingChange}>
                    <Option value="STATUS">Status</Option>
                    <OptGroup label="Delivery Deadline">
                      <Option value="WEEK_0_1">Less than a week</Option>
                      <Option value="WEEK_1_2">1–2 weeks</Option>
                      <Option value="WEEK_2_3">2–3 weeks</Option>
                      <Option value="WEEK_3_4">3–4 weeks</Option>
                      <Option value="WEEK_4_PLUS">More than a month</Option>
                    </OptGroup>
                    <Option value="NO_GROUP">No Grouping</Option>
                  </Select>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={24}>
              <HasAccess role={PERMISSIONS.items.item.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="ids"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                  }}
                  onChange={handleTableChange}
                  pagination={false}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <SuppliersDrawer onExpand={onSupplierDrawerExpand} drawerState={supplierDrawerState} data={suppliersData} />
      <ClientsDrawer onExpand={onClientDrawerExpand} drawerState={clientDrawerState} data={clientsData} />
      <PurchasePricesDrawer onExpand={onPurchasePriceDrawerExpand} drawerState={purchaseDrawerState} data={orderSku} />
    </Col>
  );
};
